//// Application Packages
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import axios from 'axios'
import {
    translateField,
    matchingEvaluationField,
    passwordEvaluationField,
} from '../../functions/field-functions'
import { extractSubmissionPage } from '../../functions/page-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateAccountData,
    updateAccountValidity,
    selectAccount,
} from '../../reducers/accountSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import TooltipComponent from '../../components/simple-components/tooltip-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

// Carbon Components
import { Form, TextInput } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const TemporaryPasswordPage = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const accountData = useSelector(selectAccount)

    //// Slice Data
    const digital_name = settingsData?.['page']?.['digital_name']
    const written_name = settingsData?.['page']?.['written_name']
    const submit_route = settingsData?.['page']?.['submit_route']
    const fields = settingsData?.['page']?.['fields']

    //////// Search Params

    //////// Methods

    // Orchestrate Temporary Password Data (Integration)
    const orchestrateTemporaryPassword = async (accountData) => {
        try {
            if (accountData) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_ACCOUNT_PASSWORD_SET}`
                const url = base_url + api_endpoint
                const { reguserid, TemporaryPassword, NewPassword } =
                    accountData

                // Create Request Based On Present Data
                if (reguserid && TemporaryPassword && NewPassword) {
                    const { data } = await axios.post(url, {
                        reguserid: reguserid,
                        current_password: TemporaryPassword,
                        new_password: NewPassword,
                    })
                    if (data) {
                        return data
                    } else {
                        console.log(
                            'Account Temporary Password: Response Is Undefined'
                        )
                        return null
                    }
                } else {
                    console.log(
                        'Account Temporary Password: Request Is Undefined'
                    )
                    return null
                }
            } else {
                console.log('Account Temporary Password: Request Is Undefined')
                return null
            }
        } catch (error) {
            console.warn('Orchestrate Temporary Password Error:', error)
            return null
        }
    }

    // Re-Validate Data (Utility)
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateAccountValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    // Validate Data (Check)
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (submit?.reguserid) {
                schema.yup = object({
                    TemporaryPassword: string()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    NewPassword: string()
                        .min(6)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        )
                        .test('match', 'New Passwords Must Match', () =>
                            matchingEvaluationField(
                                submit?.NewPassword,
                                submit?.NewPasswordConfirm
                            )
                        )
                        .test(
                            'format',
                            'New Password Must Follow Format Guidance',
                            () =>
                                passwordEvaluationField(
                                    submit?.NewPassword,
                                    submit?.reguserid
                                )
                        ),
                    NewPasswordConfirm: string()
                        .min(6)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        )
                        .test('match', 'New Passwords Must Match', () =>
                            matchingEvaluationField(
                                submit?.NewPasswordConfirm,
                                submit?.NewPassword
                            )
                        )
                        .test(
                            'format',
                            'New Password Must Follow Format Guidance',
                            () =>
                                passwordEvaluationField(
                                    submit?.NewPasswordConfirm,
                                    submit?.reguserid
                                )
                        ),
                })
                schema.valid = {
                    TemporaryPassword: false,
                    TemporaryPasswordErrMsg: '',
                    NewPassword: false,
                    NewPasswordErrMsg: '',
                    NewPasswordConfirm: false,
                    NewPasswordConfirmErrMsg: '',
                }
            } else {
                schema.yup = object({
                    TemporaryPassword: string()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    NewPassword: string()
                        .min(6)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        )
                        .test('match', 'New Passwords Must Match', () =>
                            matchingEvaluationField(
                                submit?.NewPassword,
                                submit?.NewPasswordConfirm
                            )
                        )
                        .test(
                            'format',
                            'New Password Must Follow Format Guidance',
                            () =>
                                passwordEvaluationField(
                                    submit?.NewPassword,
                                    null
                                )
                        ),
                    NewPasswordConfirm: string()
                        .min(6)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        )
                        .test('match', 'New Passwords Must Match', () =>
                            matchingEvaluationField(
                                submit?.NewPasswordConfirm,
                                submit?.NewPassword
                            )
                        )
                        .test(
                            'format',
                            'New Password Must Follow Format Guidance',
                            () =>
                                passwordEvaluationField(
                                    submit?.NewPasswordConfirm,
                                    null
                                )
                        ),
                })
                schema.valid = {
                    TemporaryPassword: false,
                    TemporaryPasswordErrMsg: '',
                    NewPassword: false,
                    NewPasswordErrMsg: '',
                    NewPasswordConfirm: false,
                    NewPasswordConfirmErrMsg: '',
                }
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    //////// Functional Methods

    // Handle Data + Redact Data (Method)
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        //// End Watson Assistant Journey
    }

    // Handle Clear Data (Soft Reset)
    const handleClearData = () => {
        //// Perform Data Clearing

        dispatch(
            updateAccountData({
                digital_name: digital_name,
                values: {
                    TemporaryPassword: '',
                    NewPassword: '',
                    NewPasswordConfirm: '',
                },
            })
        )

        //// End Data Clearing
    }

    // Handle Submission (Method)
    const handleSubmit = async () => {
        try {
            console.info('Account Temporary Password: Submitted')

            // Extract Account Data From Redux
            const submit = await extractSubmissionPage(
                digital_name,
                accountData
            )

            // Validate Account Submission Data
            const valid = await validation(submit)

            // Check If Account Data Is Valid
            if (valid) {
                console.info('Account Temporary Password: Passed Valid')
                setLoading(true)

                // Send Orchestration Request
                const orchestrate = await orchestrateTemporaryPassword(submit)

                console.info('Account Temporary Password: Passed Orchestrate')

                // Check For Password Set & Message
                if (orchestrate?.password_set) {
                    console.info('Account Temporary Password: Success')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'success',
                        title: 'Account Password Updated:',
                        subtitle: 'Account Temporary Password To New Password',
                    })

                    // Clear Existing Data
                    handleClearData()

                    // Navigate To Next Form
                    navigate(submit_route ? submit_route : '/')
                } else if (
                    orchestrate?.password_set_message ===
                    'New Account Password Invalid (Upper Case Character Required)'
                ) {
                    console.info('Account Temporary Password: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Temporary Password:',
                        subtitle:
                            'New Account Password Is Invalid Due To Missing One (1) Required Upper Case Character',
                    })
                    dispatch(
                        updateAccountValidity({
                            digital_name: digital_name,
                            errors: {
                                NewPassword: true,
                                NewPasswordErrMsg:
                                    'Missing One (1) Required Upper Case Character',
                                ConfirmNewPassword: true,
                                ConfirmNewPasswordErrMsg:
                                    'Missing One (1) Required Upper Case Character',
                            },
                        })
                    )

                    console.log(
                        'Account Temporary Password Failed: New Account Password Is Invalid Due To Missing One (1) Required Upper Case Character'
                    )
                } else if (
                    orchestrate?.password_set_message ===
                    'New Account Password Invalid (Number Required)'
                ) {
                    console.info('Account Temporary Password: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Temporary Password:',
                        subtitle:
                            'New Account Password Is Invalid Due To Missing One (1) Required Number',
                    })
                    dispatch(
                        updateAccountValidity({
                            digital_name: digital_name,
                            errors: {
                                NewPassword: true,
                                NewPasswordErrMsg:
                                    'Missing One (1) Required Number',
                                ConfirmNewPassword: true,
                                ConfirmNewPasswordErrMsg:
                                    'Missing One (1) Required Number',
                            },
                        })
                    )

                    console.log(
                        'Account Temporary Password Failed: New Account Password Is Invalid Due To Missing One (1) Required Number'
                    )
                } else if (
                    orchestrate?.password_set_message ===
                    'New Account Password Invalid (Special Character Required)'
                ) {
                    console.info('Account Temporary Password: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Temporary Password:',
                        subtitle:
                            'New Account Password Is Invalid Due To Missing One (1) Required Special Character !@#$%^&*()_+{}',
                    })
                    dispatch(
                        updateAccountValidity({
                            digital_name: digital_name,
                            errors: {
                                NewPassword: true,
                                NewPasswordErrMsg:
                                    'Missing One (1) Required Special Character !@#$%^&*()_+{}',
                                ConfirmNewPassword: true,
                                ConfirmNewPasswordErrMsg:
                                    'Missing One (1) Required Special Character !@#$%^&*()_+{}',
                            },
                        })
                    )

                    console.log(
                        'Account Temporary Password Failed: New Account Password Is Invalid Due To Missing One (1) Required Special Character !@#$%^&*()_+{}'
                    )
                } else if (
                    orchestrate?.password_set_message ===
                    'Current Account Password Invalid'
                ) {
                    console.info('Account Temporary Password: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Temporary Password:',
                        subtitle: 'Temporary Account Password Is Invalid',
                    })
                    dispatch(
                        updateAccountValidity({
                            digital_name: digital_name,
                            errors: {
                                TemporaryPassword: false,
                                TemporaryPasswordErrMsg:
                                    'Temporary Password Invalid',
                            },
                        })
                    )

                    console.log(
                        'Account Temporary Password Failed: Temporary Account Password Is Invalid'
                    )
                } else if (
                    orchestrate?.password_set_message === 'User Id Invalid'
                ) {
                    console.info('Account Temporary Password: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Temporary Password:',
                        subtitle: 'User Id Is Invalid',
                    })
                    dispatch(
                        updateAccountValidity({
                            digital_name: digital_name,
                            errors: {
                                UserID: true,
                                UserIDErrMsg: 'User Id Invalid',
                            },
                        })
                    )

                    console.log(
                        'Account Temporary Password Failed: User Id Is Invalid'
                    )
                } else {
                    console.info('Account Temporary Password: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Temporary Password:',
                        subtitle: 'Account Credentials Are Invalid',
                    })

                    console.log(
                        'Account Temporary Password Failed: Account Credentials Are Invalid'
                    )
                }
            } else {
                console.info('Account Temporary Password: Failed')

                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Account Temporary Password:',
                    subtitle:
                        'Please Fix All Validation Errors Before Trying To Resubmit',
                })

                console.log(
                    'Account Temporary Password Invalid: Validation Is Invalid'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Key Down Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <div
                    className="form"
                    data-testid="account-temporary-password-page"
                >
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ``}
                    </h2>
                    <LoadingComponent />
                </div>
            </>
        )
    } else {
        return (
            <>
                <div
                    className="form"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    data-testid="account-temporary-password-page"
                >
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ``}
                    </h2>
                    <Form onSubmit={handleSubmit}>
                        <DividerComponent text="Account Credentials" />

                        <TooltipComponent
                            text="Password Format Guidance"
                            tooltip="A minimum of 6 characters. Must contain at least one Uppercase alphabetic character. Must contain at least one Lowercase alphabetic character. Must contain at least one Number. Must contain at least one special character (Examples: !@#$%^&*()_+{}). Must not be the same as your USER ID. Must not be a variation of your USER ID."
                        />

                        <TextInput.PasswordInput
                            className="required"
                            labelText="Temporary Password:"
                            id="TemporaryPassword"
                            name="TemporaryPassword"
                            autocomplete="current-password"
                            placeholder="Temporary Password"
                            value={
                                accountData?.[digital_name]?.values
                                    ?.TemporaryPassword
                            }
                            maxLength="50"
                            helperText=""
                            hidePasswordLabel="Hide Password"
                            showPasswordLabel="Show Password"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.TemporaryPassword
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.TemporaryPasswordErrMsg
                            }
                        />

                        <TextInput.PasswordInput
                            className="required"
                            labelText="New Password:"
                            id="NewPassword"
                            name="NewPassword"
                            autocomplete="new-password"
                            placeholder="New Password"
                            value={
                                accountData?.[digital_name]?.values?.NewPassword
                            }
                            maxLength="50"
                            helperText=""
                            hidePasswordLabel="Hide Password"
                            showPasswordLabel="Show Password"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.NewPassword
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.NewPasswordErrMsg
                            }
                        />

                        <TextInput.PasswordInput
                            className="required"
                            labelText="Confirm New Password:"
                            id="NewPasswordConfirm"
                            name="NewPasswordConfirm"
                            autocomplete="new-password"
                            placeholder="Confirm New Password"
                            value={
                                accountData?.[digital_name]?.values
                                    ?.NewPasswordConfirm
                            }
                            maxLength="50"
                            helperText=""
                            hidePasswordLabel="Hide Password"
                            showPasswordLabel="Show Password"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.NewPasswordConfirm
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.NewPasswordConfirmErrMsg
                            }
                        />

                        <NotificationComponent notification={notification} />

                        <div className="button-group">
                            <div className="button-group-item">
                                <ButtonComponent
                                    click={handleSubmit}
                                    title="Submit"
                                />
                            </div>
                        </div>
                    </Form>
                </div>
                <span
                    className="visually-hidden"
                    data-testid="account-temporary-password-page-errors"
                    role="log"
                    aria-disabled="true"
                >
                    {JSON.stringify(accountData?.[digital_name]?.errors)}
                </span>
            </>
        )
    }
}

export default TemporaryPasswordPage
